import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  useMediaQuery
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import CloseIcon from '../CustomIcons/CloseIcon';
import BaseButton from '../Buttons/BaseButton';
import { SlideUpTransition } from '../Transitions/Transitions';
import LogoGdgTwoLine from '../CustomIcons/LogoGdgTwoLine';

const ContractRenameInfoDialog = ({ open, onClose }) => {
  const classes = useStyles();
  const isMobileSize = useMediaQuery(theme =>
    theme.breakpoints.down(theme.breakpoints.values.sm)
  );

  let transition;
  if (isMobileSize) {
    transition = SlideUpTransition;
  }

  const renderNameChangeCard = () => (
    <div className={classes.changeCard}>
      <div className={classes.changeColumn}>
        <Typography className={classes.changeLabel}>ANTES</Typography>
        <Typography className={classes.changeValueOld}>Suscripción</Typography>
      </div>
      <ArrowForwardIcon className={classes.arrowIcon} />
      <div className={classes.changeColumn}>
        <Typography className={classes.changeLabelNew}>AHORA</Typography>
        <Typography className={classes.changeValueNew}>Contrato</Typography>
      </div>
    </div>
  );

  const renderFormulaCard = () => (
    <div className={classes.formulaCard}>
      <Typography className={classes.formulaLine} align="center">
        N.° de suscripción anterior{' '}
        <span className={classes.formulaHighlight}>+ 3.500.000</span>
      </Typography>
      <Typography className={classes.formulaEquals} align="center">
        =
      </Typography>
      <Typography className={classes.formulaResult} align="center">
        Nuevo número de contrato
      </Typography>
    </div>
  );

  const renderExampleCard = () => (
    <div className={classes.exampleCard}>
      <Typography className={classes.exampleLabel}>
        EJEMPLO · ASÍ QUEDARÍA TU CONTRATO
      </Typography>
      <div className={classes.exampleRow}>
        <div className={classes.exampleColumn}>
          <Typography className={classes.exampleOldLabel}>
            Suscripción anterior
          </Typography>
          <Typography className={classes.exampleOldValue}>862</Typography>
        </div>
        <ArrowForwardIcon className={classes.arrowIconGreen} />
        <div className={classes.exampleColumn}>
          <Typography className={classes.exampleNewLabel}>
            Nuevo contrato
          </Typography>
          <Typography className={classes.exampleNewValue}>3.500.862</Typography>
        </div>
      </div>
    </div>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      TransitionComponent={transition}
    >
      <DialogContent className={classes.content}>
        <IconButton
          className={classes.closeButton}
          onClick={onClose}
          size="small"
          aria-label="cerrar"
        >
          <CloseIcon size={15} />
        </IconButton>

        <div className={classes.iconBox}>
          <LogoGdgTwoLine className={classes.logo} />
        </div>

        <Typography className={classes.mainTitle} align="center">
          Tu suscripción ahora es un{' '}
          <span className={classes.titleHighlight}>contrato</span>
        </Typography>

        <Typography className={classes.subtitle} align="center">
          Para brindarte un mejor servicio, hicimos dos cambios importantes en
          tu cuenta:
        </Typography>

        <div className={classes.step}>
          <div className={classes.stepHeader}>
            <div className={classes.stepNumber}>1</div>
            <Typography className={classes.stepTitle}>
              Cambió el nombre
            </Typography>
          </div>
          {renderNameChangeCard()}
        </div>

        <div className={classes.step}>
          <div className={classes.stepHeader}>
            <div className={classes.stepNumber}>2</div>
            <Typography className={classes.stepTitle}>
              Cambió el número
            </Typography>
          </div>
          <Typography className={classes.stepDesc}>
            Tu nuevo número de contrato se obtiene sumando{' '}
            <strong>3.500.000</strong> a tu antiguo número de suscripción.
          </Typography>
          {renderFormulaCard()}
        </div>

        {renderExampleCard()}

        <Typography className={classes.footer} align="center">
          No te preocupes: la forma de consultar y pagar tu factura sigue siendo
          exactamente la misma.
        </Typography>
      </DialogContent>

      <DialogActions className={classes.actions}>
        <BaseButton onClick={onClose} color="primary" size="small">
          Entendido
        </BaseButton>
      </DialogActions>
    </Dialog>
  );
};

const useStyles = makeStyles(theme => ({
  content: {
    padding: theme.spacing(4, 3, 1),
    [theme.breakpoints.down(theme.breakpoints.values.sm)]: {
      padding: theme.spacing(3, 2, 0)
    }
  },
  closeButton: {
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
    color: theme.palette.text.greyDark
  },
  iconBox: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down(theme.breakpoints.values.sm)]: {
      marginBottom: theme.spacing(1)
    }
  },
  logo: {
    width: 160,
    [theme.breakpoints.down(theme.breakpoints.values.sm)]: {
      width: 130
    }
  },
  mainTitle: {
    fontWeight: 700,
    fontSize: 18,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(1)
  },
  titleHighlight: {
    color: theme.palette.primary.main
  },
  subtitle: {
    fontSize: 13,
    color: theme.palette.text.greyDark,
    marginBottom: theme.spacing(2.5),
    [theme.breakpoints.down(theme.breakpoints.values.sm)]: {
      marginBottom: theme.spacing(1.5)
    }
  },
  step: {
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down(theme.breakpoints.values.sm)]: {
      marginBottom: theme.spacing(1.5)
    }
  },
  stepHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1)
  },
  stepNumber: {
    backgroundColor: theme.palette.primary.dark,
    color: theme.palette.common.white,
    borderRadius: '50%',
    width: 28,
    height: 28,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 13,
    fontWeight: 700,
    marginRight: theme.spacing(1),
    flexShrink: 0
  },
  stepTitle: {
    fontWeight: 700,
    fontSize: 15,
    color: theme.palette.text.primary
  },
  stepDesc: {
    fontSize: 13,
    color: theme.palette.text.greyDark,
    marginBottom: theme.spacing(1)
  },
  changeCard: {
    backgroundColor: theme.palette.background.accordion,
    border: `1px solid ${theme.palette.primary.disabled}`,
    borderRadius: theme.custom.borderRadius,
    padding: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-around',
    [theme.breakpoints.down(theme.breakpoints.values.sm)]: {
      padding: theme.spacing(1.5)
    }
  },
  changeColumn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    flex: 1
  },
  changeLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: theme.palette.text.grey,
    letterSpacing: 1,
    marginBottom: theme.spacing(0.5)
  },
  changeLabelNew: {
    fontSize: 11,
    fontWeight: 700,
    color: theme.palette.primary.main,
    letterSpacing: 1,
    marginBottom: theme.spacing(0.5)
  },
  changeValueOld: {
    color: theme.palette.text.grey,
    textDecoration: 'line-through',
    fontSize: 15,
    fontWeight: 600
  },
  changeValueNew: {
    color: theme.palette.text.primary,
    fontWeight: 700,
    fontSize: 15
  },
  arrowIcon: {
    color: theme.palette.primary.main,
    flexShrink: 0
  },
  formulaCard: {
    backgroundColor: theme.palette.background.accordion,
    border: `1px solid ${theme.palette.primary.disabled}`,
    borderRadius: theme.custom.borderRadius,
    padding: theme.spacing(2),
    [theme.breakpoints.down(theme.breakpoints.values.sm)]: {
      padding: theme.spacing(1.5)
    }
  },
  formulaLine: {
    fontSize: 13,
    color: theme.palette.text.primary,
    fontWeight: 600
  },
  formulaHighlight: {
    color: theme.palette.color.gasesGreen,
    fontWeight: 700
  },
  formulaEquals: {
    color: theme.palette.text.greyDark,
    fontSize: 15
  },
  formulaResult: {
    color: theme.palette.primary.dark,
    fontWeight: 700,
    fontSize: 15
  },
  exampleCard: {
    backgroundColor: theme.palette.success.lighter,
    border: `1px solid ${theme.palette.color.gasesGreen}`,
    borderRadius: theme.custom.borderRadius,
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    [theme.breakpoints.down(theme.breakpoints.values.sm)]: {
      padding: theme.spacing(1.5),
      marginBottom: theme.spacing(1)
    }
  },
  exampleLabel: {
    fontSize: 11,
    fontWeight: 700,
    color: theme.palette.color.gasesGreen,
    letterSpacing: 1,
    marginBottom: theme.spacing(1.5),
    textAlign: 'center'
  },
  exampleRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-around'
  },
  exampleColumn: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    flex: 1
  },
  exampleOldLabel: {
    fontSize: 12,
    color: theme.palette.text.greyDark,
    marginBottom: theme.spacing(0.5),
    textAlign: 'center'
  },
  exampleOldValue: {
    color: theme.palette.text.grey,
    textDecoration: 'line-through',
    fontSize: 16,
    fontWeight: 600
  },
  exampleNewLabel: {
    fontSize: 12,
    color: theme.palette.color.gasesGreen,
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
    textAlign: 'center'
  },
  exampleNewValue: {
    color: theme.palette.text.primary,
    fontWeight: 700,
    fontSize: 20
  },
  arrowIconGreen: {
    color: theme.palette.color.gasesGreen,
    flexShrink: 0
  },
  footer: {
    fontSize: 12,
    color: theme.palette.text.greyDark,
    marginBottom: theme.spacing(1)
  },
  actions: {
    padding: theme.spacing(1, 3, 2),
    [theme.breakpoints.down(theme.breakpoints.values.sm)]: {
      padding: theme.spacing(2, 2, 2)
    }
  }
}));

export default ContractRenameInfoDialog;
